import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { LifecycleStage } from 'src/models/lifecycle/_types';
import { fetchAllLifecycleStages } from 'src/models/lifecycle/lifecycle';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { AssessmentFormValues } from '../AssessmentNewPage';

export const AssessmentDetails = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.new.detailsTab',
  });
  const { values } = useFormikContext<AssessmentFormValues>();
  const { session } = useSession();

  // Fetch lifecycle stages
  const { data: lifecycleStages = [] } = useQuery({
    queryKey: ['lifecycleStages', session?.id],
    queryFn: fetchAllLifecycleStages,
    enabled: !!session?.id,
  });

  // Create delay options for auto-assign
  const delayOptions = [
    { value: -1, label: t('autoAssignDelay.dontAutomaticallySend') },
    { value: 0, label: t('autoAssignDelay.releaseImmediately') },
    ...Array.from({ length: 199 }, (_, i) => ({
      value: i + 1,
      label: t('autoAssignDelay.weeks', { count: i + 1 }),
    })),
  ];

  const totalPoints = values.fields?.reduce(
    (prev, f) =>
      prev +
      ('options' in f ? f.options || [] : []).reduce((prevOptVal, o) => {
        if (f.type === 'checkbox') {
          return prevOptVal + (o.points ?? 0);
        }
        if (f.type === 'dropdown') {
          return Math.max(prevOptVal, o.points ?? 0);
        }
        return 0;
      }, 0),
    0
  );

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="name"
            label={t('name')}
            placeholder={t('name')}
            required
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="description"
            label={t('description')}
            placeholder={t('description')}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="requiredAppText"
            label={t('requiredAppText')}
            placeholder={t('requiredAppTextPlaceholder', {
              name: values.name || t('name'),
            })}
            helperText={t('requiredAppTextHelper')}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Stack spacing={1}>
            <FormCheckbox
              name="scored"
              label={t('scoreAssessment')}
              tooltip={t('scoreAssessmentTooltip')}
            />
            {values.scored && (
              <Box>
                <Typography variant="body2">
                  {t('totalPoints', { count: totalPoints })}
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
        {values.scored && (
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="scoreFormula"
              label={t('assessmentFormula')}
              placeholder={t('formulaPlaceholder')}
              helperText={t('formulaHelperText')}
              fullWidth
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <FormCheckbox
            name="addClientOnCompletion"
            label={t('addClientOnCompletion')}
          />
        </Grid>
        {values.addClientOnCompletion && (
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="defaultLifecycleStageId"
              label={t('transitionLifecycleStage')}
              options={[
                { value: '', label: t('noTransition') },
                ...lifecycleStages.map((stage: LifecycleStage) => ({
                  value: stage.id,
                  label: stage.name,
                })),
              ]}
              fullWidth
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <FormCheckbox
            name="unassignAfterCompletion"
            label={t('alwaysUnassign')}
            tooltip={t('alwaysUnassignTooltip')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormCheckbox
            name="requireLogin"
            label={t('requireLogin')}
            tooltip={t('requireLoginTooltip')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormCheckbox
            name="sendEmailConfirmation"
            label={t('sendEmailConfirmation')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormCheckbox
            name="enableAssessementAnswerCheck"
            label={t('enableAnswerCheck')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormCheckbox name="makeCompletedPrivate" label={t('makePrivate')} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormCheckbox name="public" label={t('public')} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="autoAssignWeeks"
            label={t('assignAfterWeeks')}
            options={delayOptions}
            fullWidth
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
